import { normalizeMoney, serializeMoney } from '@/lib/adapters/common.adapter'
import { round } from '@/lib/helpers'
import type { MoneyType } from '@/lib/types/common.types'
import type {
  CostValueType,
  GroupedTrafficSourceCostType,
  TrafficSourceCostPayloadType,
  TrafficSourceCostPayloadV2Type,
  TrafficSourceCostServerType,
  TrafficSourceCostsType,
} from '@/lib/types/traffic-source-costs.types'

const isEmptyValue = (value: CostValueType) => value === undefined || value === null || value === ''

const hasCostValue = (data: TrafficSourceCostsType | GroupedTrafficSourceCostType) => {
  return (
    !isEmptyValue(data.cpc?.amount) ||
    !isEmptyValue(data.rateCpa) ||
    !isEmptyValue(data.invoiceRateCpc) ||
    !isEmptyValue(data.invoiceRateCpa)
  )
}

const normalizeTrafficSourceCost = (
  item: TrafficSourceCostServerType,
  storeCurrency: string,
): TrafficSourceCostsType => {
  let cpc: MoneyType
  if (typeof item.cpc === 'object' && item.cpc !== null) {
    cpc = normalizeMoney(item.cpc)
  } else {
    cpc = normalizeMoney({
      amount: item.cpc_v2?.amount ?? '0',
      currency: item.cpc_v2?.currency ?? storeCurrency,
    })
  }

  return {
    cpc,
    cpa: item.cpa,
    rateCpa: round(item.cpa * 100, 2),
    invoiceCpc: item.invoice_cpc_v2 ? normalizeMoney(item.invoice_cpc_v2) : null,
    invoiceCpa: item.invoice_cpa ? item.invoice_cpa : null,
    invoiceRateCpc: item.invoice_cpc_v2 ? normalizeMoney(item.invoice_cpc_v2).amount : null,
    invoiceRateCpa: item.invoice_cpa ? item.invoice_cpa * 100 : null,
    source: item.source,
    sourceName: item.source_name,
  }
}

export const normalizeTrafficSourcesCosts = (
  data: TrafficSourceCostServerType[],
  storeCurrency: string,
) => data.map((item) => normalizeTrafficSourceCost(item, storeCurrency))

export const combineTrafficSourceCosts = (
  data: TrafficSourceCostsType[],
  groupBySource = true,
): GroupedTrafficSourceCostType[] => {
  const groups: GroupedTrafficSourceCostType[] = []

  for (const item of data) {
    const { source, ...rest } = item
    if (!groupBySource) {
      groups.push({ ...rest, sourceIndexes: [source] })
      continue
    }

    // Combine traffic sources by their values
    const group = groups.find((group) => {
      return (
        group.cpc?.amount === item.cpc?.amount &&
        group.rateCpa === item.rateCpa &&
        group.invoiceRateCpc === item.invoiceRateCpc &&
        group.invoiceRateCpa === item.invoiceRateCpa
      )
    })

    if (group) {
      group.sourceIndexes.push(item.source)
    } else {
      groups.push({ ...rest, sourceIndexes: [source] })
    }
  }

  return groups
}

export const handleInvoiceValues = (
  data: TrafficSourceCostsType[],
  invoiceAdvertiserDirectly: boolean,
): TrafficSourceCostsType[] => {
  if (!invoiceAdvertiserDirectly) {
    return data.map((item) => {
      return {
        ...item,
        invoiceRateCpc: null,
        invoiceRateCpa: null,
      }
    })
  }

  return data
}

export const separateTrafficSourceCosts = (
  data: GroupedTrafficSourceCostType[],
): TrafficSourceCostsType[] => {
  return data.flatMap(({ sourceIndexes, ...item }) =>
    sourceIndexes.map((sourceIndex) => ({
      ...item,
      source: sourceIndex,
    })),
  )
}

const serializeTrafficSourceCost = (
  item: TrafficSourceCostsType,
  currency: string,
): TrafficSourceCostPayloadType => {
  return {
    source: item.source,
    cpc: item.cpc ? round(Number(item.cpc.amount) * 100) : 0,
    cpc_v2: serializeMoney({
      amount: item.cpc?.amount ? Number(item.cpc.amount) : 0,
      currency: item.cpc?.currency ?? currency,
    }),
    cpa: item.rateCpa ? round(Number(item.rateCpa) / 100, 6) : 0,
    invoice_cpc: isEmptyValue(item.invoiceRateCpc)
      ? null
      : round(Number(item.invoiceRateCpc) * 100),
    invoice_cpc_v2: isEmptyValue(item.invoiceRateCpc)
      ? null
      : serializeMoney({ amount: Number(item.invoiceRateCpc), currency }),
    invoice_cpa: isEmptyValue(item.invoiceRateCpa)
      ? null
      : round(Number(item.invoiceRateCpa) / 100, 6),
  }
}

const serializeTrafficSourceCostV2 = (
  item: TrafficSourceCostsType,
  currency: string,
  omitFields: string[] = [],
): TrafficSourceCostPayloadV2Type => {
  const payload = {
    source: item.source,
    cpc: serializeMoney({
      amount: item.cpc?.amount ? Number(item.cpc.amount) : 0,
      currency: item.cpc?.currency ?? currency,
    }),
    cpa: item.rateCpa ? round(Number(item.rateCpa) / 100, 6) : 0,
    invoice_cpc: isEmptyValue(item.invoiceRateCpc)
      ? null
      : round(Number(item.invoiceRateCpc) * 100),
    invoice_cpa: isEmptyValue(item.invoiceRateCpa)
      ? null
      : round(Number(item.invoiceRateCpa) / 100, 6),
  }

  omitFields.forEach((field) => {
    delete payload[field as keyof TrafficSourceCostPayloadV2Type]
  })

  return payload
}

export const serializeTrafficSourceCosts = (
  data: TrafficSourceCostsType[] | GroupedTrafficSourceCostType[] = [],
  currency: string,
): TrafficSourceCostPayloadType[] => {
  const trafficSourceCosts: TrafficSourceCostPayloadType[] = []
  let costs: TrafficSourceCostsType[]

  if (data[0] && 'sourceIndexes' in data[0]) {
    const groups = data as GroupedTrafficSourceCostType[]
    costs = separateTrafficSourceCosts(groups)
  } else {
    costs = data as TrafficSourceCostsType[]
  }

  costs.forEach((item) => {
    if (hasCostValue(item)) {
      trafficSourceCosts.push(serializeTrafficSourceCost(item, currency))
    }
  })

  return trafficSourceCosts
}

export const serializeTrafficSourceCostsV2 = (
  data: TrafficSourceCostsType[] | GroupedTrafficSourceCostType[] = [],
  defaultCurrency: string,
  omitFields: string[] = [],
): TrafficSourceCostPayloadV2Type[] => {
  const trafficSourceCosts: TrafficSourceCostPayloadV2Type[] = []
  let costs: TrafficSourceCostsType[]

  if (data[0] && 'sourceIndexes' in data[0]) {
    const groups = data as GroupedTrafficSourceCostType[]
    costs = separateTrafficSourceCosts(groups)
  } else {
    costs = data as TrafficSourceCostsType[]
  }

  costs.forEach((item) => {
    if (hasCostValue(item)) {
      trafficSourceCosts.push(serializeTrafficSourceCostV2(item, defaultCurrency, omitFields))
    }
  })

  return trafficSourceCosts
}
